import React, { useMemo, useState } from "react";
import {
  FunnelChart,
  Funnel,
  LabelList,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import "../../../styles/charts/PipelineStageDistributionChart.scss";
import CommonSelect from "../../../../../../core/common/commonSelect";
import { formatNumber } from "../../../helpers/numbers.helper";

interface PipelineStageDistributionProps {
  data: any;
}

const PipelineStageDistributionChart: React.FC<
  PipelineStageDistributionProps
> = ({ data }) => {
  const [selectedPipeline, setSelectedPipeline] = useState<string>(
    data?.pipelines?.[0]?.id?.toString() || ""
  );

  const { chartData, totalLeads } = useMemo(() => {
    if (!selectedPipeline || !data?.stageDistribution?.[selectedPipeline]) {
      return { chartData: [], totalLeads: 0 };
    }

    const pipelineData = data.stageDistribution[selectedPipeline];
    const filteredStages = pipelineData.stages
      .filter((stage: any) => stage.count > 0)
      .map((stage: any) => ({
        name: stage.stage_name,
        value: stage.count,
        fill: stage.stage_color || "#3b82f6",
        percentage: stage.percentage,
      }));

    return {
      chartData: filteredStages,
      totalLeads: pipelineData.total_leads,
    };
  }, [data, selectedPipeline]);

  const hasData = chartData.length > 0;
  const dynamicHeight = Math.max(chartData.length * 100, 400);

  const pipelineOptions = data?.pipelines?.map((pipeline: any) => ({
    value: pipeline?.id?.toString(),
    label: pipeline?.pipeline_name,
  }));

  const CustomLabel = (props: any) => {
    const { x, y, width, height, name, value } = props;

    return (
      <g>
        <text
          x={x + width / 2}
          y={y + height / 2 - 8}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={14}
          fontWeight={700}
          style={{ textShadow: "0px 1px 4px rgba(0,0,0,0.8)" }}>
          {name}: {formatNumber(value)}
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;

      return (
        <div className="funnel-tooltip">
          <div className="tooltip-header">
            <p className="tooltip-stage-name">{data?.name}</p>
          </div>
          <div className="tooltip-content">
            <div className="tooltip-item">
              <span className="tooltip-label">Total:</span>
              <span className="tooltip-value">
                {formatNumber(data?.value || 0)}
              </span>
            </div>
            <div className="tooltip-item">
              <span className="tooltip-label">Percentage:</span>
              <span className="tooltip-value">{data?.percentage || 0}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="funnel-chart-container">
      <div className="chart-header">
        <CommonSelect
          className="select small text-muted"
          options={pipelineOptions}
          defaultValue={
            selectedPipeline
              ? pipelineOptions?.find(
                  (pipeline: any) => pipeline.value === selectedPipeline
                )
              : pipelineOptions?.[0]
          }
          onChange={(selected: any) => {
            setSelectedPipeline(selected.value);
          }}
          placeHolder="Select Pipeline"
        />
      </div>

      <div className="chart-wrapper">
        {hasData ? (
          <div className="chart-scroll-container">
            <ResponsiveContainer width="100%" height={dynamicHeight}>
              <FunnelChart margin={{ top: 0, right: 10, bottom: 0, left: 10 }}>
                <Tooltip content={<CustomTooltip />} />
                <Funnel
                  dataKey="value"
                  data={chartData}
                  isAnimationActive={true}
                  animationDuration={800}
                  animationBegin={0}>
                  <LabelList position="center" content={<CustomLabel />} />
                  {chartData.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.fill}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  ))}
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="no-data-message">
            <i className="ti ti-database-off"></i>
            <p>No stage distribution data available!</p>
          </div>
        )}
      </div>

      {hasData && (
        <div className="chart-footer">
          <div className="stage-legend">
            {chartData.map((stage: any, index: number) => (
              <div key={index} className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: stage.fill }}
                />
                <span className="legend-name">{stage.name}</span>
                <span className="legend-count">
                  {formatNumber(stage.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineStageDistributionChart;
